import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateTime, formatTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QrCheckin } from "@/components/QrCheckin";
import { SessionAttendanceDialog } from "@/components/SessionAttendanceDialog";
import { useLive } from "@/lib/useLive";
import { cachedGet } from "@/lib/attQueue";
import { PendingSyncBar } from "@/components/SessionAttendanceDialog";
import { Loader2, QrCode, ClipboardCheck, Users, Megaphone, CalendarDays, Sun } from "lucide-react";

function TodayCard({ s, onOpen }) {
  const pct = s.students ? Math.round((s.present / s.students) * 100) : 0;
  return (
    <Card className="p-4" data-testid={`hoy-session-${s.session_id}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-primary text-primary-foreground px-3 py-2 text-center shrink-0">
          <p className="text-lg font-extrabold leading-none">{formatTime(s.starts_at)}</p>
          <p className="text-[10px] mt-1 opacity-80">{s.duration_min} min</p>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold line-clamp-2">{s.workshop_name}</p>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid={`hoy-count-${s.session_id}`}>{s.present} de {s.students} reserva(s) presentes</p>
          <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>
      <Button className="w-full mt-3 rounded-full" onClick={() => onOpen(s)} data-testid={`hoy-open-${s.session_id}`}><ClipboardCheck className="w-4 h-4 mr-1.5" />Pasar lista y pagos</Button>
    </Card>
  );
}

function FollowersCard({ onGo }) {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/business/followers").then((r) => setF(r.data)).catch(() => {}); }, []);
  return (
    <Card className="p-4 flex items-center gap-3" data-testid="hoy-followers">
      <span className="grid place-items-center w-11 h-11 rounded-full bg-sun/20 text-[#7a5600] shrink-0"><Users className="w-5 h-5" /></span>
      <div className="flex-1 min-w-0">
        <p className="font-extrabold text-xl leading-none" data-testid="hoy-followers-count">{f ? f.count : "—"}</p>
        <p className="text-xs text-muted-foreground mt-1">Alumnos te siguen{f?.new_7d ? ` · +${f.new_7d} esta semana` : ""}</p>
      </div>
      <Button size="sm" variant="outline" className="rounded-full" onClick={onGo} data-testid="hoy-go-comunidad"><Megaphone className="w-4 h-4 mr-1.5" />Avisar</Button>
    </Card>
  );
}

export function MaestroHoy({ onGoComunidad }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [scan, setScan] = useState(false);
  const load = useCallback(() => cachedGet("/business/today").then((r) => setData(r.data)).catch(() => setData((d) => d || { today: [], next: [] })), []);
  useEffect(() => { load(); }, [load]);
  useLive(load, 8000);
  return (
    <div className="pt-4 space-y-5 max-w-2xl" data-testid="maestro-hoy">
      <PendingSyncBar />
      <Card className="p-4 bg-primary/5 border-primary/30">
        {scan ? <><QrCheckin onScanned={load} /><Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setScan(false)} data-testid="hoy-close-scan">Cerrar escáner</Button></>
          : <Button size="lg" className="w-full rounded-full h-14 text-base" onClick={() => setScan(true)} data-testid="hoy-open-scan"><QrCode className="w-5 h-5 mr-2" />Escanear QR de asistencia</Button>}
      </Card>
      <div>
        <h3 className="font-bold mb-2.5 flex items-center gap-1.5"><Sun className="w-4 h-4 text-sun" />Sesiones de hoy</h3>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /> : data.today.length ? (
          <div className="space-y-3">{data.today.map((s) => <TodayCard key={s.session_id} s={s} onOpen={setOpen} />)}</div>
        ) : <p className="text-sm text-muted-foreground rounded-2xl border border-dashed p-5 text-center" data-testid="hoy-empty">Hoy no tienes sesiones.</p>}
      </div>
      {data?.next.length > 0 && (
        <div data-testid="hoy-next">
          <h3 className="font-bold mb-2.5 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-primary" />Próximas</h3>
          <div className="divide-y rounded-2xl border">
            {data.next.map((s) => (
              <button key={s.session_id} type="button" onClick={() => setOpen(s)} className="w-full text-left p-3 flex items-center gap-3 hover:bg-muted/60 transition-colors" data-testid={`hoy-next-${s.session_id}`}>
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{s.workshop_name}</p><p className="text-xs text-muted-foreground">{formatDateTime(s.starts_at)}</p></div>
                <span className="text-xs font-semibold text-primary shrink-0">{s.students} reserva(s)</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <FollowersCard onGo={onGoComunidad} />
      {open && <SessionAttendanceDialog w={{ id: open.workshop_id, name: open.workshop_name }} sessionId={open.session_id} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}
